import { TFunction } from 'i18next';
import { OrderPageIntent } from '../OrderPageIntent';
import { PassScheduleSelectionType, PaymentFormType } from '../../api';
import { sellsBothVoucherTypes, shouldShowDisclaimersStep } from '../utils/stepHelpers';
import { isProjectSelectionInBooking } from '../../projectSelection/projectSelectionUtils';
import { isFixedScheduleMonthly } from '../../managePassesPage/utils/passUtils';
import { IntentConfig, SaveStrategy, StepId } from './types';

export const getIntentConfigs = (t: TFunction): Partial<Record<OrderPageIntent, IntentConfig>> => ({
  [OrderPageIntent.BUY_NOW]: {
    intent: OrderPageIntent.BUY_NOW,
    steps: [
      { id: StepId.DETAILS, stepperLabel: t('order_page.step1') },
      {
        id: StepId.PROJECT_SELECTION,
        stepperLabel: t('order_page.step_project_selection'),
        condition: (ctx) => isProjectSelectionInBooking(ctx.orderPage),
      },
      { id: StepId.PERSONAL_DETAILS, stepperLabel: t('order_page.step3') },
      {
        id: StepId.DISCLAIMERS,
        stepperLabel: t('order_page.step_disclaimers'),
        condition: (ctx) => shouldShowDisclaimersStep(ctx.orderPage, ctx.language),
      },
      { id: StepId.PAYMENT, stepperLabel: t('order_page.step4') },
    ],
    requiresDateSelection: true,
    hasSessionTimeout: true,
    canSwitchToGiftCard: true,
    saveStrategy: SaveStrategy.ORDER,
    paymentFormType: PaymentFormType.ORDER,
  },
  [OrderPageIntent.GIFT_CARD]: {
    intent: OrderPageIntent.GIFT_CARD,
    steps: [
      { id: StepId.DETAILS, stepperLabel: t('order_page.step1') },
      {
        id: StepId.GIFT_CARD_TYPE,
        stepperLabel: t('order_page.stepGiftCardType'),
        condition: (ctx) => sellsBothVoucherTypes(ctx.orderPage),
      },
      { id: StepId.GIFT_CARD_PERSONAL_DETAILS, stepperLabel: t('order_page.stepVoucher3') },
      {
        id: StepId.DISCLAIMERS,
        stepperLabel: t('order_page.step_disclaimers'),
        condition: (ctx) => shouldShowDisclaimersStep(ctx.orderPage, ctx.language),
      },
      { id: StepId.PAYMENT, stepperLabel: t('order_page.step4') },
    ],
    requiresDateSelection: false,
    hasSessionTimeout: false,
    canSwitchToGiftCard: false,
    saveStrategy: SaveStrategy.VOUCHER,
    paymentFormType: PaymentFormType.VOUCHER,
  },
  [OrderPageIntent.MONETARY_GIFT_CARD]: {
    intent: OrderPageIntent.MONETARY_GIFT_CARD,
    steps: [
      {
        id: StepId.DETAILS,
        stepperLabel: t('order_page.step1'),
        condition: (ctx) => !ctx.isStandaloneGiftCard,
      },
      {
        id: StepId.GIFT_CARD_TYPE,
        stepperLabel: t('order_page.stepGiftCardType'),
        condition: (ctx) => sellsBothVoucherTypes(ctx.orderPage) && !ctx.isStandaloneGiftCard,
      },
      { id: StepId.GIFT_CARD_AMOUNT, stepperLabel: t('order_page.stepGiftCardAmount') },
      { id: StepId.GIFT_CARD_PERSONAL_DETAILS, stepperLabel: t('order_page.stepVoucher3') },
      {
        id: StepId.DISCLAIMERS,
        stepperLabel: t('order_page.step_disclaimers'),
        condition: (ctx) => shouldShowDisclaimersStep(ctx.orderPage, ctx.language),
      },
      { id: StepId.PAYMENT, stepperLabel: t('order_page.step4') },
    ],
    requiresDateSelection: false,
    hasSessionTimeout: false,
    canSwitchToGiftCard: false,
    saveStrategy: SaveStrategy.VOUCHER,
    paymentFormType: PaymentFormType.VOUCHER,
  },
  [OrderPageIntent.REDEEM_GIFT_CARD]: {
    intent: OrderPageIntent.REDEEM_GIFT_CARD,
    steps: [
      { id: StepId.VOUCHER_SUMMARY, stepperLabel: t('order_page.step1') },
      { id: StepId.DATE_AND_TIME, stepperLabel: t('order_page.step2') },
      { id: StepId.PERSONAL_DETAILS, stepperLabel: t('order_page.step3') },
      { id: StepId.REDEMPTION_SUMMARY, stepperLabel: t('order_page.step4') },
    ],
    requiresDateSelection: true,
    hasSessionTimeout: false,
    canSwitchToGiftCard: false,
    saveStrategy: SaveStrategy.ORDER,
    paymentFormType: null,
  },
  [OrderPageIntent.BUY_PASS]: {
    intent: OrderPageIntent.BUY_PASS,
    steps: [
      { id: StepId.PASS_SUMMARY, stepperLabel: t('order_page.step1') },
      { id: StepId.PERSONAL_DETAILS, stepperLabel: t('order_page.step3') },
      {
        id: StepId.PASS_SCHEDULE_SELECTIONS,
        stepperLabel: t('order_page.step_pass_schedule'),
        condition: (ctx) =>
          ctx.orderPage?.schedule_selection_type === PassScheduleSelectionType.WEEKLY_SLOTS ||
          isFixedScheduleMonthly(ctx.orderPage?.pass_type),
      },
      {
        id: StepId.PASS_DATED_SESSION_SELECTIONS,
        stepperLabel: t('order_page.step_pass_schedule'),
        condition: (ctx) => ctx.orderPage?.schedule_selection_type === PassScheduleSelectionType.DATED_SESSIONS,
      },
      {
        id: StepId.DISCLAIMERS,
        stepperLabel: t('order_page.step_disclaimers'),
        condition: (ctx) => shouldShowDisclaimersStep(ctx.orderPage, ctx.language),
      },
      { id: StepId.PAYMENT, stepperLabel: t('order_page.step4') },
    ],
    requiresDateSelection: false,
    hasSessionTimeout: false,
    canSwitchToGiftCard: false,
    saveStrategy: SaveStrategy.PASS,
    paymentFormType: PaymentFormType.PASS,
  },
});
